<template>
  <Layout>
    <Head :title="__('Reopen Ticket')" />

    <div class="max-w-3xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
      <!-- Header -->
      <div class="mb-8">
        <h1 class="text-2xl font-bold text-gray-900">
          {{ __('Reopen Ticket') }} #{{ ticket.uid }}
        </h1>
        <p class="mt-2 text-sm text-gray-600">
          {{ __('Reopen this closed ticket to continue working on it') }}
        </p>
      </div>

      <!-- Form -->
      <div class="bg-white shadow rounded-lg overflow-hidden">
        <div class="p-6">
          <!-- Status Selection -->
          <div class="mb-6">
            <label class="block text-sm font-medium text-gray-700 mb-1">
              {{ __('New Status *') }}
            </label>
            <select
              v-model="form.status_id"
              :error="form.errors.status_id"
              class="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              required
            >
              <option value="" disabled>{{ __('Select a status') }}</option>
              <option
                v-for="status in reopenStatuses"
                :key="status.id"
                :value="status.id"
              >
                {{ status.name }}
              </option>
            </select>
            <p v-if="form.errors.status_id" class="mt-1 text-sm text-red-600">
              {{ form.errors.status_id }}
            </p>
          </div>

          <!-- Reason Field -->
          <div class="mb-6">
            <label class="block text-sm font-medium text-gray-700 mb-1">
              {{ __('Reason for Reopening *') }}
            </label>
            <textarea
              v-model="form.reason"
              :error="form.errors.reason"
              class="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              rows="5"
              required
              :placeholder="__('Explain why you are reopening this ticket...')"
            ></textarea>
            <p v-if="form.errors.reason" class="mt-1 text-sm text-red-600">
              {{ form.errors.reason }}
            </p>
          </div>

          <!-- Action Buttons -->
          <div class="flex justify-end space-x-3">
            <Link
              :href="route('tickets.edit', ticket.uid)"
              class="btn-gray"
            >
              {{ __('Cancel') }}
            </Link>
            <LoadingButton
              @click="submit"
              :loading="form.processing"
              class="btn-green"
            >
              {{ form.processing ? __('Reopening...') : __('Reopen Ticket') }}
            </LoadingButton>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import { Head, Link } from '@inertiajs/vue3'
import Layout from '@/Shared/Layout'
import LoadingButton from '@/Shared/LoadingButton'

export default {
  components: {
    Head,
    Layout,
    Link,
    LoadingButton,
  },
  props: {
    ticket: Object,
    statuses: Array,
  },
  data() {
    return {
      form: this.$inertia.form({
        status_id: this.statuses.find(s => !s.slug.includes('closed'))?.id || null,
        reason: '',
      }),
    }
  },
  computed: {
    reopenStatuses() {
      return this.statuses.filter(status => !status.slug.includes('closed'))
    },
  },
  methods: {
    submit() {
      this.form.post(this.route('tickets.reopen', this.ticket.id), {
        preserveScroll: true,
        onSuccess: () => {
          this.$toast.success(__('Ticket reopened successfully'))
        },
        onError: () => {
          this.$toast.error(__('Failed to reopen ticket'))
        }
      })
    },
  },
}
</script>

<style scoped>
.btn-gray {
  @apply inline-flex items-center px-4 py-2 bg-gray-200 border border-transparent rounded-md font-semibold text-xs text-gray-800 uppercase tracking-widest hover:bg-gray-300 focus:bg-gray-300 active:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition ease-in-out duration-150;
}

.btn-green {
  @apply inline-flex items-center px-4 py-2 bg-green-600 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-green-700 focus:bg-green-700 active:bg-green-800 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition ease-in-out duration-150;
}
</style>
